import * as React from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Modal — slot-pattern, native `<dialog>`-element.
 *
 * **Pattern**: parent kontrolliert `open`, Modal ruft `showModal()`/`close()`
 * via effect synchron mit dem prop. Native `<dialog>`-element regelt:
 *   - focus-trap (browser-builtin)
 *   - ESC-close (cancel-event → onClose)
 *   - aria-modal + role="dialog" (implizit)
 *   - inert-handling für background
 *
 * **Manuell zu handlen**:
 *   - backdrop-click (click direkt auf dialog-element, nicht children)
 *   - busy-state (ESC + backdrop + X während mutation blocken)
 *   - close-X (slot-button im header)
 *
 * **Animation**: CSS-only via `@starting-style` + `transition-behavior:
 * allow-discrete` in `main.css` (Baseline 2024). Reduced-motion-fallback
 * dort definiert.
 *
 * **Bundle-impact**: ~2 KB raw / ~0.7 KB gzip — kein focus-trap-lib.
 */
export interface ModalProps {
  /** Open-state; parent kontrolliert lifecycle (kein internal-state). */
  open: boolean;
  /** Close-handler — wird bei ESC, backdrop-click und X-button gefeuert. */
  onClose: () => void;
  /** Titel — rendert als `<h2>` für aria-labelledby. */
  title: string;
  /** Optional: beschreibungs-text unter dem titel. */
  description?: string;
  /** Body-content. */
  children: React.ReactNode;
  /** Footer-slot — typischerweise [Abbrechen] [Primary]. */
  footer?: React.ReactNode;
  /** Disable close-mechanisms (ESC, backdrop, X) während mutation. */
  busy?: boolean;
  /** Max-width auf desktop (default "md" = 28rem). */
  size?: "sm" | "md" | "lg";
  /** Optional kontextuelle id für aria-labelledby (sonst auto via useId). */
  labelledById?: string;
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  busy = false,
  size = "md",
  labelledById,
}: ModalProps): React.ReactElement {
  const dialogRef = React.useRef<HTMLDialogElement>(null);
  const autoId = React.useId();
  const headingId = labelledById ?? `${autoId}-title`;

  // Sync open-prop → dialog method.
  React.useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    if (open && !dlg.open) dlg.showModal();
    if (!open && dlg.open) dlg.close();
  }, [open]);

  // ESC (cancel-event) + backdrop-click handling.
  React.useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    const onCancel = (e: Event) => {
      if (busy) {
        e.preventDefault();
        return;
      }
      onClose();
    };
    const onClick = (e: MouseEvent) => {
      if (busy) return;
      // Backdrop-click = click direkt auf <dialog>-element (nicht children).
      if (e.target === dlg) onClose();
    };
    dlg.addEventListener("cancel", onCancel);
    dlg.addEventListener("click", onClick);
    return () => {
      dlg.removeEventListener("cancel", onCancel);
      dlg.removeEventListener("click", onClick);
    };
  }, [onClose, busy]);

  const maxW = size === "sm" ? "sm:max-w-sm" : size === "lg" ? "sm:max-w-2xl" : "sm:max-w-md";

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={headingId}
      className={cn(
        "hp-modal m-auto w-[calc(100%-2rem)] rounded-[var(--radius-xl)]",
        "bg-[color:var(--color-surface)] p-0 shadow-[var(--shadow-lg)]",
        "border-0 backdrop:bg-black/40",
        "max-sm:m-0 max-sm:h-full max-sm:max-h-none max-sm:w-full max-sm:rounded-none",
        maxW,
      )}
    >
      <header className="flex items-start justify-between gap-4 p-6 pb-2">
        <div className="min-w-0">
          <h2
            id={headingId}
            className="text-xl font-semibold text-[color:var(--color-text-primary)]"
          >
            {title}
          </h2>
          {description && (
            <p className="mt-1 text-sm text-[color:var(--color-text-secondary)]">
              {description}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          aria-label="Dialog schließen"
          className={cn(
            "-m-2 inline-flex size-11 shrink-0 items-center justify-center rounded-md",
            "text-[color:var(--color-text-secondary)]",
            "transition-colors duration-150 ease-out",
            "hover:bg-[color:var(--color-surface-sunken)]",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
        >
          <X aria-hidden="true" className="size-5" strokeWidth={2} />
        </button>
      </header>
      <div className="px-6 py-4 max-sm:flex-1 max-sm:overflow-y-auto">{children}</div>
      {footer && (
        <footer
          className={cn(
            "flex flex-col-reverse gap-2 border-t border-[color:var(--color-border)]",
            "p-4 sm:flex-row sm:justify-end sm:p-6 sm:pt-4",
          )}
        >
          {footer}
        </footer>
      )}
    </dialog>
  );
}

Modal.displayName = "Modal";
